import { useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { ResetClientPasswordDialog } from '../../components/ResetClientPasswordDialog';

interface ClientDetail {
  id: number; email: string; full_name: string; phone: string | null; status: string; access_level: string; created_at: string;
}
interface ExchangeAccount {
  id: number; provider: string; label: string | null; status: string; access_mode: string | null; created_at: string;
}

const CLIENT_STATUSES = ['ACTIVE', 'PENDING', 'INACTIVE', 'SUSPENDED'];
const ACCESS_LEVELS = [
  { value: 'TRADE', label: 'Trade' },
  { value: 'VIEW_ONLY', label: 'View Only' },
];
const ACCOUNT_STATUSES = ['CONNECTED', 'DISCONNECTED', 'INVALID', 'DISABLED'];
const PROVIDERS = ['BINANCE', 'BYBIT', 'DERIBIT'];

const accessColor: Record<string, string> = { TRADE: '#16c784', VIEW_ONLY: '#f5a623' };

const statusColor: Record<string, string> = {
  ACTIVE: '#16c784', CONNECTED: '#16c784', PENDING: '#f5a623', DISCONNECTED: '#f5a623',
  INACTIVE: '#8b97b0', DISABLED: '#8b97b0', SUSPENDED: '#ea3943', INVALID: '#ea3943',
};

function errText(e: unknown, fallback: string) {
  return (e as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ?? fallback;
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const key = ['client', id];
  const [pwResetOpen, setPwResetOpen] = useState(false);

  const { data } = useQuery({
    queryKey: key,
    queryFn: async () =>
      (await api.get(`/clients/${id}`)).data.data as { client: ClientDetail; exchangeAccounts: ExchangeAccount[] },
  });

  const client = data?.client;
  const accounts = data?.exchangeAccounts ?? [];

  const setStatus = useMutation({
    mutationFn: (status: string) => api.patch(`/clients/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const setAccessLevel = useMutation({
    mutationFn: (accessLevel: string) => api.patch(`/clients/${id}/access-level`, { accessLevel }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const setAccountStatus = useMutation({
    mutationFn: ({ accountId, status }: { accountId: number; status: string }) =>
      api.patch(`/clients/${id}/exchange-accounts/${accountId}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const setAccountAccessMode = useMutation({
    mutationFn: ({ accountId, accessMode }: { accountId: number; accessMode: string }) =>
      api.patch(`/clients/${id}/exchange-accounts/${accountId}/access-mode`, { accessMode }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  // --- Add exchange account form ---
  const emptyForm = { provider: 'BINANCE', label: '', apiKey: '', apiSecret: '', passphrase: '' };
  const [form, setForm] = useState(emptyForm);

  const testConn = useMutation({
    mutationFn: async () =>
      (await api.post(`/clients/${id}/exchange-accounts/test`, {
        provider: form.provider, apiKey: form.apiKey, apiSecret: form.apiSecret, passphrase: form.passphrase || undefined,
      })).data.data as { ok: boolean; message: string },
  });
  const addAccount = useMutation({
    mutationFn: () => api.post(`/clients/${id}/exchange-accounts`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key });
      setForm(emptyForm);
      testConn.reset();
    },
  });

  const set = (k: keyof typeof form, v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    // Editing credentials invalidates any previous test result.
    if (['provider', 'apiKey', 'apiSecret', 'passphrase'].includes(k)) testConn.reset();
  };
  const credsReady = form.apiKey.length >= 8 && form.apiSecret.length >= 8;

  if (!client) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/clients')} color="inherit">
          Back to clients
        </Button>
        <Typography sx={{ mt: 4, color: 'text.secondary' }}>Loading client…</Typography>
      </Box>
    );
  }

  const cColor = statusColor[client.status] ?? '#8b97b0';
  const aColor = accessColor[client.access_level] ?? '#8b97b0';

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/clients')} color="inherit" sx={{ mb: 2 }}>
        Back to clients
      </Button>

      {/* Profile + status */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
          <Avatar sx={{ width: 56, height: 56, fontSize: 22, fontWeight: 700, background: 'linear-gradient(135deg,#4f8cff,#16c784)' }}>
            {client.full_name?.[0]?.toUpperCase() ?? '?'}
          </Avatar>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h5">{client.full_name}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {client.email}{client.phone ? ` · ${client.phone}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>STATUS</Typography>
            <Select
              size="small"
              value={client.status}
              onChange={(e) => setStatus.mutate(e.target.value)}
              disabled={setStatus.isPending}
              sx={{ minWidth: 150, color: cColor, fontWeight: 700, '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(cColor, 0.4) } }}
            >
              {CLIENT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>ACCESS</Typography>
            <Select
              size="small"
              value={client.access_level ?? 'TRADE'}
              onChange={(e) => setAccessLevel.mutate(e.target.value)}
              disabled={setAccessLevel.isPending}
              sx={{ minWidth: 150, color: aColor, fontWeight: 700, '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(aColor, 0.4) } }}
            >
              {ACCESS_LEVELS.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
            </Select>
            <Button variant="outlined" size="small" startIcon={<LockResetIcon />} onClick={() => setPwResetOpen(true)}>
              Reset Password
            </Button>
          </Box>
        </CardContent>
        {setStatus.isError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{errText(setStatus.error, 'Failed to update status')}</Alert>
        )}
        {setAccessLevel.isError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{errText(setAccessLevel.error, 'Failed to update access level')}</Alert>
        )}
      </Card>

      {/* Exchange accounts */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <VpnKeyIcon sx={{ color: 'primary.light' }} />
        <Typography variant="h6">Exchange Accounts</Typography>
        <Chip
          size="small"
          icon={<VerifiedUserIcon sx={{ fontSize: 15 }} />}
          label="Production"
          sx={{ color: '#16c784', backgroundColor: alpha('#16c784', 0.14), border: `1px solid ${alpha('#16c784', 0.35)}`, '& .MuiChip-icon': { color: '#16c784' } }}
        />
      </Box>

      <Card sx={{ mb: 3 }}>
        <TableContainer>
          <Table sx={{ minWidth: 620 }}>
            <TableHead>
              <TableRow>
                <TableCell>Provider</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Access Mode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Added</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{a.provider}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{a.label || '—'}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      disableUnderline
                      value={(a.access_mode as string) ?? 'APPROVAL'}
                      onChange={(e) => setAccountAccessMode.mutate({ accountId: a.id, accessMode: e.target.value })}
                      sx={{ fontWeight: 700, fontSize: 13, color: a.access_mode === 'DIRECT' ? '#16c784' : '#f5a623' }}
                    >
                      <MenuItem value="APPROVAL">Approval Required</MenuItem>
                      <MenuItem value="DIRECT">Direct (no approval)</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      disableUnderline
                      value={a.status}
                      onChange={(e) => setAccountStatus.mutate({ accountId: a.id, status: e.target.value })}
                      sx={{ fontWeight: 700, color: statusColor[a.status] ?? '#8b97b0', fontSize: 13 }}
                    >
                      {ACCOUNT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {!accounts.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                    No exchange accounts yet. Add production API keys below.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add exchange account (production API keys) */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Add API Key</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            Keys are encrypted (AES-256-GCM) at rest. Production environment only.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField select label="Exchange" value={form.provider} onChange={(e) => set('provider', e.target.value)} fullWidth>
                {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField label="Label" value={form.label} onChange={(e) => set('label', e.target.value)} fullWidth
                helperText="Distinguishes multiple accounts on the same exchange (e.g. Main, Hedge)." />
            </Box>
            <TextField label="API Key" value={form.apiKey} onChange={(e) => set('apiKey', e.target.value)} fullWidth />
            <TextField label="API Secret" type="password" value={form.apiSecret} onChange={(e) => set('apiSecret', e.target.value)} fullWidth />
            <TextField label="Passphrase (optional)" type="password" value={form.passphrase} onChange={(e) => set('passphrase', e.target.value)} fullWidth
              helperText="Required by some exchanges (e.g. options venues)." />

            {/* Test result */}
            {testConn.data && (
              <Alert
                severity={testConn.data.ok ? 'success' : 'error'}
                icon={testConn.data.ok ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
              >
                {testConn.data.message}
              </Alert>
            )}
            {testConn.isError && <Alert severity="error">{errText(testConn.error, 'Test failed')}</Alert>}
            {addAccount.isError && <Alert severity="error">{errText(addAccount.error, 'Failed to add API key')}</Alert>}
            {addAccount.isSuccess && <Alert severity="success">API key added and encrypted.</Alert>}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<NetworkCheckIcon />}
                onClick={() => testConn.mutate()}
                disabled={testConn.isPending || !credsReady}
              >
                {testConn.isPending ? 'Testing…' : 'Test Connection'}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => addAccount.mutate()}
                disabled={addAccount.isPending || !credsReady}
              >
                {addAccount.isPending ? 'Adding…' : 'Add API Key'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <ResetClientPasswordDialog
        clientId={id!}
        clientName={client.full_name}
        open={pwResetOpen}
        onClose={() => setPwResetOpen(false)}
      />
    </Box>
  );
}
